import { toast as sonnerToast } from 'sonner';

import { Notice } from '@/components/primitives/Notice';

import type { ToastOptions } from './Toast.types';
import type { NoticeKind } from '@/components/primitives/Notice';
import type * as React from 'react';

/**
 * Renders a toast as a `Notice` via sonner's headless `toast.custom`.
 * With a description the message becomes the Notice title; otherwise the
 * message is shown on its own, as in Figma's message-only Notice.
 */
const show = (kind: NoticeKind, message: React.ReactNode, options: ToastOptions = {}) => {
  const { description, action, dismissible = true, ...sonnerOptions } = options;

  return sonnerToast.custom(
    (id) => (
      <Notice
        kind={kind}
        title={description ? message : undefined}
        description={description ?? message}
        link={action?.label}
        onLinkClick={
          action
            ? () => {
                action.onClick();
                sonnerToast.dismiss(id);
              }
            : undefined
        }
        dismissible={dismissible}
        onClose={() => sonnerToast.dismiss(id)}
        // sonner's toaster section is already the live region
        role={undefined}
        aria-live={undefined}
      />
    ),
    sonnerOptions
  );
};

/**
 * Toast API — sonner's call shape, Notice styling.
 *
 * @example
 * ```ts
 * toast.success(t('Copied layer ID to clipboard'));
 * toast.warning(t('Zoom in to view zonal stats grid'), { id: 'zoom', action: { label: t('Zoom in'), onClick } });
 * toast.dismiss('zoom');
 * ```
 */
export const toast = Object.assign(
  (message: React.ReactNode, options?: ToastOptions) => show('brand', message, options),
  {
    info: (message: React.ReactNode, options?: ToastOptions) => show('info', message, options),
    success: (message: React.ReactNode, options?: ToastOptions) =>
      show('success', message, options),
    warning: (message: React.ReactNode, options?: ToastOptions) =>
      show('warning', message, options),
    error: (message: React.ReactNode, options?: ToastOptions) => show('danger', message, options),
    dismiss: sonnerToast.dismiss,
  }
);
